import { log } from 'utils/logging.ts'
import { Criticality, Severity } from 'types/logging.ts'
import { DEEP_LINK_HOSTS } from './constants.ts'

/**
 * Converts an incoming deep link URL into an in-app route path.
 * Anything off-host is rejected so a crafted link can never drive the webview to a foreign origin.
 *
 * @param {string} rawUrl - the absolute URL carried by the Capacitor appUrlOpen event
 * @returns {string | null} the path including search & hash, or null when the URL is unparseable or off-host
 */
export const getDeepLinkPath = (rawUrl: string): string | null => {
  let url: URL

  try {
    url = new URL(rawUrl)
  } catch {
    log({
      error: 'Received an unparseable deep link URL',
      identifier: '[deepLink:getDeepLinkPath]',
      metaData: { deepLink: { rawUrl } },
      severity: Severity.WARNING,
      criticality: Criticality.LOW,
      fingerprint: ['deepLink', 'unparseable']
    })

    return null
  }

  if (!DEEP_LINK_HOSTS.includes(url.hostname)) {
    log({
      error: 'Received a deep link for an unknown host',
      identifier: '[deepLink:getDeepLinkPath]',
      metaData: { deepLink: { host: url.hostname } },
      severity: Severity.WARNING,
      criticality: Criticality.LOW,
      fingerprint: ['deepLink', 'unknownHost']
    })

    return null
  }

  return `${url.pathname}${url.search}${url.hash}`
}
